/**
 * DiagramKindBadge
 *
 * A small label showing a diagram's Diagram_Kind (Excalidraw or Mermaid), so
 * the two kinds are distinguishable in the browser's grid and table
 * (Requirement 6.2). Presentational and shared by `DiagramCard` and
 * `DiagramTable` so the wording and styling stay consistent.
 */

import { PenTool, Workflow } from "lucide-react"
import { cn } from "@/lib/utils"
import type { DiagramKind } from "@/services/diagramsService"

/** The label and icon shown for each Diagram_Kind. */
const KIND_PRESENTATION: Record<
  DiagramKind,
  { label: string; icon: React.ComponentType<{ className?: string }> }
> = {
  excalidraw: { label: "Excalidraw", icon: PenTool },
  mermaid: { label: "Mermaid", icon: Workflow },
}

/** The props for {@link DiagramKindBadge}. */
export interface DiagramKindBadgeProps {
  /** The Diagram_Kind to show. */
  kind: DiagramKind
  /** Extra classes for the badge container. */
  className?: string
}

/**
 * A badge naming a diagram's kind, with a kind-specific icon.
 *
 * @param props - See {@link DiagramKindBadgeProps}.
 * @returns The rendered badge.
 */
export default function DiagramKindBadge({
  kind,
  className,
}: DiagramKindBadgeProps): React.JSX.Element {
  const { label, icon: Icon } = KIND_PRESENTATION[kind]
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-sm border border-border bg-muted/60 px-1.5 py-0.5 text-xs text-muted-foreground",
        className,
      )}
    >
      <Icon className="size-3" aria-hidden="true" />
      {label}
    </span>
  )
}
